import BestPlans from '../assets/retailHealthInsurance/best-plans.webp';
import HospitalRoom from '../assets/retailHealthInsurance/hospital-room.webp';
import MedicalSupplies from '../assets/retailHealthInsurance/medical-supplies.webp';
import KeepsGrowing from '../assets/retailHealthInsurance/keeps-growing.webp';
import RestoresItself from '../assets/retailHealthInsurance/restore-itself.webp';
import { z } from 'zod';
import { IHealthInsuranceConfig, IMemberForm } from '@/types';
import { MEMBER_PERSONAL_FIELDS } from '@/constants/fieldDescriptors/memberPersonalFields';
import { buildFieldGroupSchema } from '@/utils/buildFieldGroupSchema';
import { memberPersonalDescriptorSuperRefine } from '@/utils/fieldDescriptorSchemaRefines';

export const PLANS_BENEFITS_SCREENS = [
  {
    title: 'We only show the best plans',
    subtitle: "From India's best insurers and tailored to your needs",
    description:
      'With 1L+ claims processed each year, we know which features truly make a difference',
    button: 'Explore features',
    buttonType: 'tertiary',
    icon: BestPlans,
  },
  {
    tag: 'pick any',
    title: 'Hospital room',
    subtitle:
      'Many plans limit room choice, which impacts claim settlement',
    description:
      'Our plans let you pick any hospital room at the time of hospitalisation',
    icon: HospitalRoom,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover',
    title: 'Medical supplies',
    subtitle:
      'Most plans do not cover the cost of basic supplies like masks, syringes, and surgical items',
    description:
      'Our plans cover medical supplies thus enhancing your claims payout',
    icon: MedicalSupplies,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover that',
    title: 'Keeps growing',
    subtitle:
      'Many plans offer static cover, leaving you underinsured as costs rise',
    description: 'Our plans offer at least 50% increase in coverage annually',
    icon: KeepsGrowing,
    button: 'Skip to show all plans',
    buttonType: 'tertiary',
  },
  {
    tag: 'cover that',
    title: 'Restores itself',
    subtitle: "Most plans don't reset, leaving you vulnerable to future claims",
    description:
      "Our plans restore coverage after each claim, so you're always protected",
    button: 'Show Plans',
    buttonType: 'primary',
    icon: RestoresItself,
  },
];

export const BEST_COVERAGE_BULLET_POINTS = [
  {
    title: 'Choose any hospital room',
    subtitle: 'No restrictions at the time of hospitalisation',
  },
  {
    title: 'No co-pay, no hidden charges',
    subtitle:
      'What you see is what you get. No surprises at the time of claim settlement',
  },
  {
    title: 'Cover cost of medical supplies',
    subtitle:
      'Gloves, syringes, masks, surgical supplies—all covered to cut your out-of-pocket costs',
  },
];

export const HASSLE_FREE_BULLET_POINTS = [
  {
    title: 'Your claims, our priority',
    subtitle:
      '10,000+ claims processed monthly, 85% settled within a week. You’re in good hands',
  },
  {
    title: 'Dedicated claims buddy',
    subtitle:
      'A claims expert handles everything for you, from paperwork to approvals',
  },
  {
    title: 'We fight for fair settlements',
    subtitle:
      'Every month, we recover ₹25L+ from disputed claims for our users. Trust us to get you what’s rightfully yours',
  },
];

export const MORE_COVERAGE_BULLET_POINTS = [
  {
    title: 'Adapt to rising medical costs',
    subtitle:
      'Enjoy at least a 50% coverage increase annually, ensuring long-term protection',
  },
  {
    title: 'Tailor your cover each year',
    subtitle:
      'Adjust your cover to cover more members, increase coverage, or add features',
  },
  {
    title: 'Free Cover Evaluation & Upgrade',
    subtitle:
      'Annual assessments with a Plum expert, plus the option to upgrade your coverage with waived waiting periods!',
  },
];

export const BEST_PLUM_BULLET_POINTS = [
  {
    title: 'Lifetime access',
    subtitle:
      'Continue using Plum app and services, regardless of your employment status',
  },
  {
    title: 'On-demand claims & advisory support',
    subtitle:
      'Reach out to us for any queries on existing policies or to get additional coverage',
  },
  {
    title: 'Beyond hospital bills',
    subtitle:
      'Access doctor consultations and health checkups on the Plum app, for life',
  },
];

export const RHI_WHATSAPP_INITIAL_MESSAGE =
  `Hi Saypotree, I'd like to explore personal health insurance plans with Plum`;
export const RHI_PHONE_NUMBER = '9980469489';

export const RHI_USER_QUERY_MESSAGE = 'wanting to buy health insurance';

export const createMemberSchema = (config: IHealthInsuranceConfig) =>
  z
    .object({
      self: z.object({
        isChecked: z.boolean(),
        age: z.string(),
        name: z.string().optional(),
      }),
      spouse: z.object({
        isChecked: z.boolean(),
        age: z.string(),
        name: z.string().optional(),
      }),
      children: z.array(
        z.object({
          isChecked: z.boolean(),
          age: z.string(),
          name: z.string().optional(),
        }),
      ),
      parents: z.array(
        z.object({
          isChecked: z.boolean(),
          age: z.string(),
          name: z.string().optional(),
        }),
      ),
      pincode: z.string().optional(),
      isParentIncluded: z.boolean().optional(),
    })
    .superRefine((data, ctx) => {
      if (!config) return;

      if (data.self.isChecked) {
        if (!data.self.age.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Age is mandatory for self',
            path: ['self', 'age'],
          });
        } else {
          const age = Number(data.self.age);
          if (isNaN(age)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Age must be a number',
              path: ['self', 'age'],
            });
          } else if (age < config.ageConstraints.adult.min.value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Age must be at least ${config.ageConstraints.adult.min.value}`,
              path: ['self', 'age'],
            });
          } else if (age > 120) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Age must be less than or equal to 120`,
              path: ['self', 'age'],
            });
          }
        }
      }

      // Validate spouse age only if showSpouse is true
      if (data.spouse.isChecked) {
        if (!data.spouse.age.trim()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Age is mandatory for spouse',
            path: ['spouse', 'age'],
          });
        } else {
          const age = Number(data.spouse.age);
          if (isNaN(age)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Age must be a number',
              path: ['spouse', 'age'],
            });
          } else if (age < config.ageConstraints.adult.min.value) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Age must be at least ${config.ageConstraints.adult.min.value}`,
              path: ['spouse', 'age'],
            });
          } else if (age > 120) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: `Age must be less than or equal to 120`,
              path: ['spouse', 'age'],
            });
          }
        }
      }

      data.children.forEach((child, index) => {
        if (child.isChecked) {
          if (!child.age.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Age is mandatory for child',
              path: ['children', index, 'age'],
            });
          } else {
            const age = Number(child.age);
            if (isNaN(age)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Age must be a number',
                path: ['children', index, 'age'],
              });
            } else if (age < config.ageConstraints.child.min.value) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Age must be at least ${config.ageConstraints.child.min.value}`,
                path: ['children', index, 'age'],
              });
            } else if (age >= config.ageConstraints.child.max.value) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Age must be less than ${config.ageConstraints.child.max.value}`,
                path: ['children', index, 'age'],
              });
            }
          }
        }
      });

      data.parents.forEach((parent, index) => {
        if (parent.isChecked) {
          if (!parent.age.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Age is mandatory for parent',
              path: ['parents', index, 'age'],
            });
          } else {
            const age = Number(parent.age);
            if (isNaN(age)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Age must be a number',
                path: ['parents', index, 'age'],
              });
            } else if (age < config.ageConstraints.adult.min.value) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Age must be at least ${config.ageConstraints.adult.min.value}`,
                path: ['parents', index, 'age'],
              });
            } else if (age > 120) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `Age must be less than or equal to 120`,
                path: ['parents', index, 'age'],
              });
            }
          }
        }
      });
    });

export const FAQ = [
  {
    question:
      'Why should I buy personal health insurance if I already have corporate coverage?',
    answer: `Corporate insurance ends when you switch jobs and doesn't grow with your needs or inflation. Personal health insurance stays with you, offering long-term, flexible coverage that adapts to your changing requirements.`,
  },
  {
    question: 'Can I switch to Plum if I already have health insurance?',
    answer:
      'Yes! Plum offers a free evaluation of your current policy and guides you to the best plan. We help you port your policy, maintaining continuity benefits like waiting period waivers.',
  },
  {
    question: 'What is the best time to buy personal health insurance?',
    answer: `The best time is when you're healthy and young. Premiums are lower, and you're more likely to be approved. Waiting could lead to higher costs or rejection.`,
  },
  {
    question: 'How much does personal health insurance cost?',
    answer: `Premiums start at approximately ₹1,000/month for ₹5 lakh coverage, depending on age, medical history, and location. Costs rise with age or health issues, but it's an essential investment.`,
  },
  {
    question: 'How soon can I make a claim after purchasing the insurance?',
    answer:
      'Emergency claims can be made after 31 days. For pre-existing conditions, there is usually a waiting period of 2-4 years, depending on the specific plan. Accidents are covered immediately from day 1.',
  },
];

export const TESTIMONIALS = [
  {
    title: 'They are genuinely invested in helping you',
    subTitle:
      'They assisted me with both medical and life insurance, and they were very patient in explaining the details and benefits. It feels like they are genuinely invested in helping you make informed decisions.',
    userName: 'Kawaljeet Singh',
    userIcon: '',
    userAge: '33 years',
    insurer: 'HDFC Ergo',
    planType: 'Optima Secure',
  },
  {
    title: `I've had an incredible experience with Plum Personal`,
    subTitle: `As someone who values both affordability and comprehensive care, I've had an incredible experience with Plum Personal. The process was straightforward and the team has been nothing short of helpful, clarifying every query patiently.`,
    userName: 'Madri Mohan Prasad',
    userIcon: '',
    userAge: '38 years',
    insurer: 'ICICI Lombard',
    planType: 'Elevate',
  },
  {
    title: 'The process revolved around consultative approach',
    subTitle: `The process revolved around consultative approach and curating a plan which was more suitable for me. Krishna was super helpful during the process`,
    userName: 'Alan Fernandes',
    userIcon: '',
    userAge: '38 years',
    insurer: 'ICICI Lombard',
    planType: 'Elevate',
  },
  {
    title: 'In an industry filled with jargons, the process was quite simple',
    subTitle: `In an industry filled with jargons, the process was quite simple. Plum  was super helpful during the entire journey`,
    userName: 'Saurabh Moody',
    userIcon: '',
    userAge: '41 years',
    insurer: 'HDFC Ergo',
    planType: 'Optima Secure',
  },
];

export const questionAnswerSchema = z.object({
  questionId: z.string(),
  answer: z.union([z.string(), z.array(z.string()), z.number()]),
  otherAnswer: z.string().optional(),
});

const memberPersonalBaseSchema = buildFieldGroupSchema(
  MEMBER_PERSONAL_FIELDS,
) as z.ZodObject<z.ZodRawShape>;

export const memberFormSchema: z.ZodType<IMemberForm> = memberPersonalBaseSchema
  .merge(
    z.object({
      id: z.string(),
      answers: z.array(questionAnswerSchema),
      relation: z.string(),
    }),
  )
  .superRefine(
    memberPersonalDescriptorSuperRefine,
  ) as unknown as z.ZodType<IMemberForm>;

export const proposalFormSchema = z.object({
  members: z.array(memberFormSchema),
});

